import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  EsOntologyAlias,
  useCreateEsAlias,
  useUpdateEsAlias,
} from "@/service-hooks/use-get-es-ontology-service";

interface EsAliasDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  indexId: string;
  indexName?: string;
  existing?: EsOntologyAlias;
  onSaved?: () => void;
}

interface FormState {
  name: string;
  filter: string;
  isWriteIndex: boolean;
}

const EMPTY: FormState = {
  name: "",
  filter: "",
  isWriteIndex: false,
};

export default function EsAliasDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  indexId,
  indexName,
  existing,
  onSaved,
}: EsAliasDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createAlias = useCreateEsAlias();
  const updateAlias = useUpdateEsAlias();
  const isEdit = !!existing?.id;
  const isPending = createAlias.isPending || updateAlias.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: existing?.name ?? "",
      filter: existing?.filter ?? "",
      isWriteIndex: !!existing?.isWriteIndex,
    });
  }, [isOpen, existing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      if (isEdit && existing?.id) {
        await updateAlias.mutateAsync({
          id: existing.id,
          projectUuid,
          dataLakeId,
          data: {
            filter: form.filter.trim() || undefined,
            isWriteIndex: form.isWriteIndex,
          },
        });
      } else {
        await createAlias.mutateAsync({
          indexId,
          projectUuid,
          dataLakeId,
          data: {
            name: form.name.trim(),
            filter: form.filter.trim() || undefined,
            isWriteIndex: form.isWriteIndex,
          },
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit ES Alias" : "Add ES Alias"}
            {indexName ? ` — ${indexName}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the alias fields for the selected ES index.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="alias name"
              className="bg-surface text-white"
              disabled={isEdit}
            />
            {isEdit && (
              <p className="text-xs text-muted">
                Alias name is the MERGE key and cannot be renamed.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Filter (JSON)</label>
            <Textarea
              value={form.filter}
              onChange={(e) => update("filter", e.target.value)}
              placeholder='{"term":{"status":"active"}}'
              className="bg-surface text-white min-h-[80px] font-mono text-xs"
            />
            <p className="text-xs text-muted">
              JSON-encoded filter clause exactly as it appears in the mapping dump.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-white">
            <Switch
              checked={form.isWriteIndex}
              onCheckedChange={(v) => update("isWriteIndex", !!v)}
            />
            Write Index
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.name.trim() || isPending}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
